import { memo, useState } from 'react';
import { Button, Checkbox, Label } from '@@/ui/primitives';
import { ListFilter, SlidersVerticalIcon, X } from 'lucide-react';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterSection {
  key: string;
  label: string;
  options: FilterOption[];
}

interface FilterPanelProps {
  sections: FilterSection[];
  selectedFilters: Record<string, Set<string>>;
  onFilterChange: (sectionKey: string, value: string) => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
}

const FilterSectionComponent = memo(
  ({
    section,
    selectedValues,
    onFilterChange
  }: {
    section: FilterSection;
    selectedValues: Set<string>;
    onFilterChange: (value: string) => void;
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
      <div className='border-b border-border last:border-b-0'>
        <button
          type='button'
          onClick={() => setIsExpanded(!isExpanded)}
          className='flex items-center justify-between py-3 w-full text-left hover:bg-muted/50 cursor-pointer -mx-2 px-2 rounded transition-colors'
        >
          <span className='text-sm text-foreground'>{section.label}</span>
          <ListFilter className='h-4 w-4 text-muted-foreground' />
        </button>
        {isExpanded && (
          <div className='pb-3 space-y-2'>
            {section.options.map((option) => (
              <Label key={option.value} className='flex items-center gap-2 cursor-pointer'>
                <Checkbox
                  checked={selectedValues.has(option.value)}
                  onCheckedChange={() => onFilterChange(option.value)}
                />
                <span className='text-sm text-foreground'>{option.label}</span>
              </Label>
            ))}
          </div>
        )}
      </div>
    );
  }
);

export const FilterPanel = memo(
  ({ sections, selectedFilters, onFilterChange, onClearFilters, onApplyFilters }: FilterPanelProps) => {
    return (
      <div className='bg-card rounded-lg border border-border p-4 min-w-56 h-fit'>
        <h4 className='text-xl font-semibold text-foreground mb-4'>Filters</h4>
        <div className='space-y-1'>
          {sections.map((section) => (
            <FilterSectionComponent
              key={section.key}
              section={section}
              selectedValues={selectedFilters[section.key] ?? new Set()}
              onFilterChange={(value) => onFilterChange(section.key, value)}
            />
          ))}
        </div>
        <div className='flex gap-2 mt-4 flex-col lg:flex-row'>
          <Button variant='outline' size='sm' onClick={onClearFilters} className='flex-1 text-xs'>
            <X className='h-3 w-3' />
            Clear Filters
          </Button>
          <Button size='sm' onClick={onApplyFilters} className='flex-1 text-xs'>
            <SlidersVerticalIcon className='h-3 w-3' />
            Apply Filters
          </Button>
        </div>
      </div>
    );
  }
);
